import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import te from './te.json';
import hi from './hi.json';
import publicTe from './public.te.json';
import { startDomTranslate } from './domTranslate';

/*
 * English, Telugu and Hindi, switchable at the desk.
 *
 * The dictionary is keyed by the English string itself rather than by invented
 * keys like `nav.billing`. Two reasons. The app already has 1,278 distinct
 * labels across 161 files, and renaming every one of them into a key namespace
 * is a very large edit with a lot of room to put the wrong key in the wrong
 * place. And a missing key fails safely here: a string the dictionary has never
 * seen comes back in English, which is exactly what it says today, instead of
 * rendering `nav.billing` at a customer.
 *
 * The owner asked for transliteration rather than translation — English words
 * written in Telugu letters, so "Billing" is బిల్లింగ్ and not a Telugu word
 * for billing. Staff already know the English terms; this lets them read them.
 * Hindi follows the same rule: बिलिंग, not a Hindi word for billing.
 *
 * hi.json is built from a word-level table rather than written phrase by
 * phrase, which is why a word reads the same on every screen: "Total" cannot
 * be one spelling in the header and another in the footer by accident.
 */

const LanguageContext = createContext({
  lang: 'en', setLang: () => {}, t: (s) => s, isPublic: false,
});

const STAFF_DICTS = { te, hi };

/*
 * The customer screens get their own Telugu, and start in it.
 *
 * Two things are different about them. They open in Telugu, because the people
 * holding the phone are customers in Vinukonda and English is the second
 * language on the counter, not the first — staff keep their own setting, which
 * is English, and the two must not reach across and change each other.
 *
 * And the words are translated rather than transliterated. The staff
 * dictionary writes English terms in Telugu letters on purpose, because staff
 * already know the English ones; a customer reading "Total" as టోటల్ has been
 * given nothing. So this dictionary is used on its own, not merged with the
 * staff one — which also means a service the salon typed in English stays
 * exactly as they typed it, instead of being transliterated into something
 * they never wrote.
 */
const PUBLIC_DICTS = { te: publicTe };
const PUBLIC_PATH = /^\/(checkin|book)(\/|$)/;

const STORAGE = 'salon.lang';
const PUBLIC_STORAGE = 'salon.lang.public';

const read = (key, fallback) => {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
};

export function LanguageProvider({ children }) {
  const location = useLocation();
  const isPublic = PUBLIC_PATH.test(location.pathname);

  // A per-device choice: the receptionist's machine stays in Telugu without
  // deciding anything for the owner's.
  const [staffLang, setStaffLang] = useState(() => read(STORAGE, 'en'));
  const [publicLang, setPublicLang] = useState(() => read(PUBLIC_STORAGE, 'te'));

  const lang = isPublic ? publicLang : staffLang;
  const dicts = isPublic ? PUBLIC_DICTS : STAFF_DICTS;

  const setLang = useCallback((next) => {
    if (isPublic) {
      setPublicLang(next);
      try { localStorage.setItem(PUBLIC_STORAGE, next); } catch { /* private window */ }
      return;
    }
    setStaffLang(next);
    try { localStorage.setItem(STORAGE, next); } catch { /* private window */ }
  }, [isPublic]);

  useEffect(() => {
    document.documentElement.lang = lang;
    const dict = dicts[lang];
    if (!dict) return undefined;
    // Covers every screen, including the ones that never call t().
    return startDomTranslate(dict);
  }, [lang, dicts]);

  const t = useCallback((text) => {
    if (lang === 'en' || typeof text !== 'string' || !text) return text;
    const dict = dicts[lang];
    if (!dict) return text;
    // Exact match first, then the same string with its surrounding whitespace
    // trimmed — JSX often leaves a newline around a label.
    return dict[text] || dict[text.trim()] || text;
  }, [lang, dicts]);

  const value = useMemo(() => ({ lang, setLang, t, isPublic }), [lang, setLang, t, isPublic]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLang() {
  return useContext(LanguageContext);
}

/** Just the translator, for the many places that need nothing else. */
export function useT() {
  return useContext(LanguageContext).t;
}

export default LanguageContext;
